import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import Header from "./Header";
import BottomNav from "./BottomNav";
import { RoyalAssistant } from "./RoyalAssistant";
import BetCartBar from "./BetCartBar";
import OfflineQueueBanner from "./OfflineQueueBanner";
import { OfflineResultAlerts } from "./OfflineResultAlerts";
import { chimeForNewWins, playWinChime } from "../lib/winChime";
import FirstRunLanguage from "./FirstRunLanguage";
import { WinCelebration } from "./WinCelebration";
import { FirstLoginPopup } from "./FirstLoginPopup";
import { say } from "../lib/voice";
import { EmptyState } from "./Common";
import { SwipeRow } from "./SwipeRow";
import { setThemeOverrides, setScenes } from "../lib/gameTheme";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { dateTimeLabel } from "../lib/format";
import { Sheet, SheetContent } from "./ui/sheet";
import { toast } from "sonner";

const WIN_KEY = "rl_celebrated_wins";

export default function CustomerLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [config, setConfig] = useState(null);
  const [win, setWin] = useState(null);

  const seenIds = React.useRef(null);
  const loadNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
      chimeForNewWins(data);
      // wallet-affecting notifications → instant balance refresh (no manual reload)
      const WALLET_TYPES = ["recharge_approved", "winning_credit", "win_auto", "gift", "bonus", "referral", "withdrawal_paid", "withdrawal_rejected", "admin_credit"];
      if (seenIds.current) {
        const fresh = data.filter((n) => !seenIds.current.has(n.id) && WALLET_TYPES.some((t) => (n.type || "").includes(t)));
        if (fresh.length) { window.dispatchEvent(new Event("rl-wallet-changed")); toast.success(fresh[0].title, { description: fresh[0].body?.slice(0, 90) }); say(`${fresh[0].title}. ${fresh[0].body || ""}`.slice(0, 200)); }
      }
      seenIds.current = new Set(data.map((n) => n.id));
    } catch {}
  }, [user]);
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => { if (document.visibilityState === "visible") loadNotifs(); }, 15000);
    return () => clearInterval(id);
  }, [user, loadNotifs]);
  useEffect(() => {
    const onMsg = (e) => { if (e.data?.type === "WIN_CHIME") playWinChime(); };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMsg);
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => { setConfig(r.data); setThemeOverrides(r.data?.category_themes); setScenes(r.data?.category_scenes); }).catch(() => {});
  }, []);

  useEffect(() => {
    loadNotifs();
  }, [loadNotifs]);

  // Detect newly-won bets and celebrate (first visit is silent to avoid spamming past wins)
  useEffect(() => {
    if (!user) return;
    api.get("/bets/my?tab=won").then(({ data }) => {
      const wonIds = (data || []).map((b) => b.id);
      let seen;
      try { seen = JSON.parse(localStorage.getItem(WIN_KEY)); } catch { seen = null; }
      if (!Array.isArray(seen)) {
        // first ever load: mark all current wins as seen, no popup
        localStorage.setItem(WIN_KEY, JSON.stringify(wonIds));
        return;
      }
      const fresh = (data || []).filter((b) => !seen.includes(b.id));
      if (fresh.length > 0) {
        const amount = fresh.reduce((s, b) => s + (b.win_amount || 0), 0);
        const games = [...new Set(fresh.map((b) => b.game_name).filter(Boolean))];
        const big = fresh.reduce((a, b) => ((b.win_amount || 0) > (a?.win_amount || 0) ? b : a), null);
        setWin({ amount, count: fresh.length, games, betId: big?.id });
        say(`வாழ்த்துக்கள்! ${games.join(", ")} — நீங்க ${Math.round(amount)} ரூபாய் ஜெயிச்சீங்க. பணம் வேணுமா, இல்ல விளையாடுறீங்களா?`);
      }
      localStorage.setItem(WIN_KEY, JSON.stringify([...new Set([...seen, ...wonIds])]));
    }).catch(() => {});
  }, [user]);

  const openNotifs = async () => {
    if (!user) {
      toast.info("Login to view notifications");
      return;
    }
    setNotifOpen(true);
    await loadNotifs();
    api.post("/notifications/read-all").then(loadNotifs).catch(() => {});
  };
  const undoTimers = React.useRef({});
  const dismissOne = (id) => {
    const item = notifs.find((n) => n.id === id);
    setNotifs((p) => p.filter((n) => n.id !== id));
    undoTimers.current[id] = setTimeout(() => { delete undoTimers.current[id]; api.post(`/notifications/${id}/dismiss`).catch(() => {}); }, 5000);
    toast("Notification removed", { duration: 5000, description: item?.title?.slice(0, 60), action: { label: "Undo", onClick: () => { clearTimeout(undoTimers.current[id]); delete undoTimers.current[id]; setNotifs((p) => (p.some((n) => n.id === id) || !item ? p : [...p, item].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")))); } } });
  };
  const dismissAll = () => { setNotifs([]); api.post("/notifications/dismiss-all").then(loadNotifs).catch(() => {}); };

  const openSupport = () => {
    const link = config?.support_link || "https://wa.me/919999999999";
    window.open(link, "_blank");
  };

  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="rl-frame pb-[130px]">
      <FirstRunLanguage />
      <Header onNotifications={openNotifs} onSupport={openSupport} unread={unread} />
      <OfflineQueueBanner />
      <OfflineResultAlerts />
      <main className="px-3 pt-3">
        <Outlet context={{ config }} />
      </main>
      <BetCartBar />
      <BottomNav />
      <RoyalAssistant />
      <FirstLoginPopup />

      {win && <WinCelebration amount={win.amount} count={win.count} games={win.games} onClose={() => setWin(null)} onShare={win.betId ? () => { setWin(null); navigate(`/my-bets?tab=won&card=${win.betId}`); } : undefined} />}

      <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
        <SheetContent side="right" className="rl-glass border-slate-200 w-[85%] max-w-sm overflow-y-auto" data-testid="notifications-sheet">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><Bell className="w-5 h-5 text-blue-600" />Notifications</h3>
            {notifs.length > 0 && <button onClick={dismissAll} className="text-[11px] font-bold text-red-500 flex items-center gap-1" data-testid="notifications-clear-all"><Trash2 className="w-3.5 h-3.5" />Clear all</button>}
          </div>
          {notifs.length === 0 ? (
            <EmptyState icon={Bell} title="No notifications" subtitle="You're all caught up!" />
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] text-slate-400 text-center" data-testid="notifications-swipe-hint">← swipe பண்ணா போயிடும் →</p>
              {notifs.map((n) => (
                <SwipeRow key={n.id} testId={`notification-${n.id}`} onDismiss={() => dismissOne(n.id)} onTap={n.url ? () => { setNotifOpen(false); if (/^https?:\/\//.test(n.url)) window.open(n.url, "_blank", "noopener"); else navigate(n.url); } : undefined}>
                <div role={n.url ? "button" : undefined} tabIndex={n.url ? 0 : undefined} onKeyDown={(e) => e.key === "Enter" && n.url && navigate(n.url)}
                  className={`rl-card p-3 bg-white ${n.url ? "cursor-pointer border-l-4 border-l-amber-400" : ""}`} data-testid={`notification-card-${n.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-slate-900">{n.title}</span>
                    {n.read && <CheckCheck className="w-3.5 h-3.5 text-emerald-400" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{n.body}</p>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[10px] text-slate-500">{dateTimeLabel(n.created_at)}</span>
                    {n.url && <span className="text-[10px] font-bold text-blue-600" data-testid={`notification-open-${n.id}`}>{n.url.includes("receipt=") ? "Open receipt ›" : "Open ›"}</span>}
                  </div>
                </div>
                </SwipeRow>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
